/**
 * Transport for SNES Composition Engine
 *
 * Manages playback state and dispatches scheduled commands to instruments.
 * Uses lookahead scheduling pattern from Web Audio best practices.
 */

import type { Project } from '../model/types.js';
import { getBeatsPerBar, getProjectDuration, asBeats } from '../model/types.js';
import { Scheduler, type SchedulerConfig, type SchedulerCommand } from './Scheduler.js';

// =============================================================================
// Types
// =============================================================================

/**
 * Transport state.
 */
export type TransportState = 'stopped' | 'playing' | 'paused';

/**
 * Playback instrument interface that can receive commands from the transport.
 * The S-DSP synth adapter implements this interface.
 */
export interface PlaybackInstrument {
  /**
   * Called when a note starts.
   * @param voice Voice index (0-7 for S-DSP)
   * @param instrumentId Instrument ID (for sample/envelope lookup)
   * @param midiNote MIDI note number (0-127)
   * @param velocity Velocity 0-127
   * @param time AudioContext time to start the note
   */
  scheduleNoteOn(
    voice: number,
    instrumentId: string,
    midiNote: number,
    velocity: number,
    time: number
  ): void;

  /**
   * Called when a note ends.
   * @param voice Voice index
   * @param time AudioContext time to end the note
   */
  scheduleNoteOff(voice: number, time: number): void;

  /**
   * Get the current AudioContext time.
   */
  getCurrentTime(): number;

  /**
   * Synchronize time tracking with the worklet.
   * Called when playback starts to establish the time reference.
   */
  syncTime?(): void;

  /**
   * Clear all pending scheduled commands.
   * Called on stop/seek to prevent stale commands from executing.
   */
  clearScheduledCommands?(): void;

  /**
   * Load a sample for an instrument.
   * @param instrumentId The instrument ID from the project
   * @param sampleData Sample data
   * @param options Sample options (loop points, etc.)
   */
  loadInstrumentSample?(
    instrumentId: string,
    sampleData: Float32Array,
    options: { loopStart?: number; loopEnd?: number; loopEnabled?: boolean; baseNote?: number }
  ): void;
}

/**
 * Transport configuration.
 */
export interface TransportConfig {
  /** Lookahead time in milliseconds (default 150) */
  lookaheadMs?: number;
  /** Scheduler callback interval in milliseconds (default 50) */
  callbackIntervalMs?: number;
}

// =============================================================================
// Transport
// =============================================================================

/**
 * Transport controls playback of a project through an instrument.
 *
 * Design:
 * - Uses lookahead scheduling (~150ms ahead)
 * - setInterval callback schedules events in time windows
 * - Instrument-agnostic: any synth implementing Instrument interface works
 */
export class Transport {
  private readonly scheduler: Scheduler;
  private readonly config: Required<TransportConfig>;

  private project: Project | null = null;
  private instrument: PlaybackInstrument | null = null;

  private state: TransportState = 'stopped';
  private schedulerConfig: SchedulerConfig | null = null;
  private lastScheduledBeat = 0;
  private intervalId: ReturnType<typeof setInterval> | null = null;

  // Position tracking
  private positionBeat = 0;

  // Loop state
  private loop = false;

  // Callbacks
  private onStateChange?: (state: TransportState) => void;
  private onPositionChange?: (beat: number) => void;
  private onLoopChange?: (loop: boolean) => void;

  constructor(config: TransportConfig = {}) {
    this.scheduler = new Scheduler(8);
    this.config = {
      lookaheadMs: config.lookaheadMs ?? 150,
      callbackIntervalMs: config.callbackIntervalMs ?? 50,
    };
  }

  /**
   * Set the project to play.
   */
  setProject(project: Project): void {
    const wasPlaying = this.state === 'playing';
    if (wasPlaying) {
      this.pause();
    }

    this.project = project;
    this.scheduler.reset();
    this.lastScheduledBeat = this.positionBeat;

    if (wasPlaying) {
      this.play();
    }
  }

  /**
   * Set the instrument to send commands to.
   */
  setInstrument(instrument: PlaybackInstrument): void {
    this.instrument = instrument;
  }

  /**
   * Get current state.
   */
  getState(): TransportState {
    return this.state;
  }

  /**
   * Get current position in beats.
   */
  getPositionBeat(): number {
    if (this.state === 'playing' && this.schedulerConfig && this.instrument) {
      // Calculate current position from audio time
      const currentTime = this.instrument.getCurrentTime();
      const elapsedSeconds = currentTime - this.schedulerConfig.startTimeSeconds;
      const elapsedBeats = (elapsedSeconds * this.schedulerConfig.tempo) / 60;
      return this.schedulerConfig.startBeat + elapsedBeats;
    }
    return this.positionBeat;
  }

  /**
   * Get current position in bars.
   */
  getPositionBar(): number {
    if (!this.project) return 0;
    const beatsPerBar = getBeatsPerBar(this.project.timeSignature);
    return this.getPositionBeat() / beatsPerBar;
  }

  /**
   * Start playback.
   */
  play(): void {
    if (!this.project || !this.instrument) {
      console.warn('Transport: Cannot play without project and instrument');
      return;
    }

    if (this.state === 'playing') return;

    // Sync the worklet's time tracking with AudioContext
    this.instrument.syncTime?.();

    const currentTime = this.instrument.getCurrentTime();

    this.schedulerConfig = {
      tempo: this.project.tempo,
      startTimeSeconds: currentTime,
      startBeat: this.positionBeat,
    };

    this.lastScheduledBeat = this.positionBeat;
    this.state = 'playing';
    this.onStateChange?.(this.state);

    // Start scheduler loop
    this.startSchedulerLoop();
  }

  /**
   * Pause playback (keeps position).
   */
  pause(): void {
    if (this.state !== 'playing') return;

    // Save current position
    this.positionBeat = this.getPositionBeat();

    this.stopSchedulerLoop();
    this.state = 'paused';
    this.onStateChange?.(this.state);

    // Send note-offs for any currently playing notes
    this.stopAllNotes();
  }

  /**
   * Stop playback (resets position to start).
   */
  stop(): void {
    this.stopSchedulerLoop();
    this.stopAllNotes();

    // Clear any pending scheduled commands in the worklet
    this.instrument?.clearScheduledCommands?.();

    this.positionBeat = 0;
    this.lastScheduledBeat = 0;
    this.scheduler.reset();
    this.schedulerConfig = null;

    this.state = 'stopped';
    this.onStateChange?.(this.state);
    this.onPositionChange?.(0);
  }

  /**
   * Seek to a position in beats.
   */
  seekToBeat(beat: number): void {
    const wasPlaying = this.state === 'playing';

    if (wasPlaying) {
      this.stopSchedulerLoop();
      this.stopAllNotes();
    }

    // Clear any pending scheduled commands in the worklet
    this.instrument?.clearScheduledCommands?.();

    this.positionBeat = Math.max(0, beat);
    this.lastScheduledBeat = this.positionBeat;
    this.scheduler.reset();

    this.onPositionChange?.(this.positionBeat);

    if (wasPlaying) {
      // Restart with new position
      this.schedulerConfig = null;
      this.state = 'paused'; // Temporarily pause so play() works
      this.play();
    }
  }

  /**
   * Seek to a position in bars.
   */
  seekToBar(bar: number): void {
    if (!this.project) return;
    const beatsPerBar = getBeatsPerBar(this.project.timeSignature);
    this.seekToBeat(bar * beatsPerBar);
  }

  /**
   * Enable or disable looping.
   */
  setLoop(enabled: boolean): void {
    this.loop = enabled;
    this.onLoopChange?.(enabled);
  }

  /**
   * Check if looping is enabled.
   */
  isLooping(): boolean {
    return this.loop;
  }

  /**
   * Set callback for state changes.
   */
  setOnStateChange(callback: (state: TransportState) => void): void {
    this.onStateChange = callback;
  }

  /**
   * Set callback for position changes (called during playback).
   */
  setOnPositionChange(callback: (beat: number) => void): void {
    this.onPositionChange = callback;
  }

  /**
   * Set callback for loop state changes.
   */
  setOnLoopChange(callback: (loop: boolean) => void): void {
    this.onLoopChange = callback;
  }

  /**
   * Get the scheduler (for inspection/testing).
   */
  getScheduler(): Scheduler {
    return this.scheduler;
  }

  // -------------------------------------------------------------------------
  // Private
  // -------------------------------------------------------------------------

  private startSchedulerLoop(): void {
    if (this.intervalId) return;

    // Initial schedule
    this.scheduleAhead();

    // Set up recurring callback
    this.intervalId = setInterval(() => {
      this.scheduleAhead();
      this.updatePosition();
    }, this.config.callbackIntervalMs);
  }

  private stopSchedulerLoop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  private scheduleAhead(): void {
    if (!this.project || !this.instrument || !this.schedulerConfig) return;

    const currentTime = this.instrument.getCurrentTime();
    const lookaheadSeconds = this.config.lookaheadMs / 1000;
    const scheduleUntilTime = currentTime + lookaheadSeconds;

    // Convert to beats
    const elapsedSeconds = scheduleUntilTime - this.schedulerConfig.startTimeSeconds;
    const scheduleUntilBeat =
      this.schedulerConfig.startBeat + (elapsedSeconds * this.schedulerConfig.tempo) / 60;

    // Get project duration for end-of-song detection
    const projectDuration = asBeats(getProjectDuration(this.project));

    // Get commands in range [lastScheduledBeat, scheduleUntilBeat)
    if (scheduleUntilBeat > this.lastScheduledBeat) {
      const commands = this.scheduler.getCommandsInRange(
        this.project,
        this.schedulerConfig,
        this.lastScheduledBeat,
        scheduleUntilBeat
      );

      // Dispatch commands to instrument
      for (const cmd of commands) {
        this.dispatchCommand(cmd);
      }

      this.lastScheduledBeat = scheduleUntilBeat;
    }

    // Check for end-of-song
    const currentBeat = this.getPositionBeat();
    if (projectDuration > 0 && currentBeat >= projectDuration) {
      if (this.loop) {
        // Loop back to the beginning
        this.seekToBeat(0);
      } else {
        // Stop playback
        this.stop();
      }
    }
  }

  private dispatchCommand(cmd: SchedulerCommand): void {
    if (!this.instrument) return;

    switch (cmd.type) {
      case 'noteOn': {
        // Convert velocity 0-127 to 0-1 range for now
        // In the future, velocity mapping will be per-instrument
        const velocity = cmd.velocity / 127;

        // Pass MIDI note directly to instrument - it has the baseNote for pitch calculation
        this.instrument.scheduleNoteOn(cmd.voice, cmd.instrumentId, cmd.pitch, velocity, cmd.time);
        break;
      }

      case 'noteOff':
        this.instrument.scheduleNoteOff(cmd.voice, cmd.time);
        break;
    }
  }

  private stopAllNotes(): void {
    if (!this.instrument) return;

    const currentTime = this.instrument.getCurrentTime();

    // Get all active notes from scheduler and send note-offs
    const activeNotes = this.scheduler.getScheduledNotes();
    for (const note of activeNotes) {
      const voiceIndex = this.scheduler.getVoiceAllocator().findVoiceByNoteId(note.noteId);
      if (voiceIndex !== -1) {
        this.instrument.scheduleNoteOff(voiceIndex, currentTime);
      }
    }

    // Also send note-offs to all voices just to be safe
    for (let i = 0; i < 8; i++) {
      this.instrument.scheduleNoteOff(i, currentTime);
    }
  }

  private updatePosition(): void {
    if (this.state === 'playing') {
      this.onPositionChange?.(this.getPositionBeat());
    }
  }
}
